import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserCircleIcon, ArrowRightOnRectangleIcon, ChevronDownIcon, Bars3Icon, XMarkIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import BlackLogoTextSBS from '../assets/logo/Black Logo + Text SBS.png';
import { fetchWithAuth } from '../services/api';
import { clearAuthentication, hasValidAuthentication } from '../services/auth';
import './Navbar.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userFullName, setUserFullName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userData, setUserData] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);

  const currentLang = location.pathname.split('/')[1] || 'mk';

  const languages = [
    { code: 'mk', name: 'Македонски', icon: 'https://flagcdn.com/w20/mk.png' },
    { code: 'al', name: 'Shqip', icon: 'https://flagcdn.com/w20/al.png' },
    { code: 'en', name: 'English', icon: 'https://flagcdn.com/w20/us.png' },
    { code: 'tr', name: 'Türkçe', icon: 'https://flagcdn.com/w20/tr.png' },
    { code: 'hr', name: 'Hrvatski', icon: 'https://flagcdn.com/w20/hr.png' },
    { code: 'sr', name: 'Српски', icon: 'https://flagcdn.com/w20/rs.png' },
    { code: 'de', name: 'Deutsch', icon: 'https://flagcdn.com/w20/de.png' }
  ];

  const fetchUserData = async () => {
    try {
      const response = await fetchWithAuth('/users/me');
      setUserData(response);
      return response;
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUserData(null);
      return null;
    }
  };


  useEffect(() => {
    const checkLoginStatus = () => {
      const fullName = localStorage.getItem('userFullName');
      const role = localStorage.getItem('userRole');
      const loggedIn = hasValidAuthentication();
      setIsLoggedIn(loggedIn);
      setUserFullName(loggedIn ? fullName || 'User' : '');
      setUserRole(loggedIn ? role || '' : '');
      
      // Fetch user data if logged in and setup language
      if (loggedIn) {
        fetchUserData().then((userData) => {
          if (userData && userData.defaultLanguage) {
            const langCodeMap = {
              'MACEDONIAN': 'mk',
              'ALBANIAN': 'al',
              'TURKISH': 'tr',
              'ENGLISH': 'en',
              'GERMAN': 'de',
              'SERBIAN': 'sr',
              'CROATIAN': 'hr'
            };
            
            const userLangCode = langCodeMap[userData.defaultLanguage] || 'mk';
            const storedLanguage = localStorage.getItem('selectedLanguage');
            const isFreshLogin = localStorage.getItem('freshLogin') === 'true';
            
            // On fresh login, use user's default language (ignore stored language)
            // On subsequent page loads, use stored language to preserve user's session choice
            const targetLanguage = isFreshLogin ? userLangCode : (storedLanguage || userLangCode);
            
            // Clear fresh login flag after using it
            if (isFreshLogin) {
              localStorage.removeItem('freshLogin');
            }
            
            // Only change language if it's different from current
            if (currentLang !== targetLanguage) {
              i18n.changeLanguage(targetLanguage);
              const currentPath = location.pathname;
              const pathWithoutLang = currentPath.replace(/^\/(mk|al|en|tr|hr|sr|de)/, '');
              navigate(`/${targetLanguage}${pathWithoutLang || '/'}`);
            }
          }
        }).catch(error => {
          console.error('Failed to setup language based on user preference:', error);
        });
      } else {
        setUserData(null);
      }
    };
    
    checkLoginStatus();
    window.addEventListener('storage', checkLoginStatus);
    window.addEventListener('authChange', checkLoginStatus);
    
    // Listen for profile updates
    const handleProfileUpdate = () => {
      if (isLoggedIn) {
        fetchUserData();
      }
    };
    
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener('authChange', checkLoginStatus);
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [isLoggedIn]);


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode) => {
    i18n.changeLanguage(langCode);
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(mk|al|en|tr|hr|sr|de)/, '');
    navigate(`/${langCode}${pathWithoutLang || '/'}`);
    setIsLangMenuOpen(false);
    setIsMobileMenuOpen(false);

    // If user is logged in, persist the language preference
    if (isLoggedIn) {
      try {
        await fetchWithAuth('/users/me/language', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: `language=${langCode}`
        });
      } catch (error) {
        console.warn('Failed to persist language preference:', error);
        // Don't block the UI if this fails
      }
    }

    // Store language preference in localStorage for session persistence
    localStorage.setItem('selectedLanguage', langCode);
  };

  const handleLogout = () => {
    clearAuthentication();
    setIsLoggedIn(false);
    setUserFullName('');
    setUserRole('');
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    window.dispatchEvent(new Event('storage'));
    navigate(`/${currentLang}/`);
  };
  
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const navClass = `sticky top-0 z-50 w-full transition-all duration-300 ease-in-out ${isScrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-sm'}`;
  const selectedLanguage = languages.find(lang => lang.code === currentLang) || languages[0];

  // Check if user can create more profiles - more robust logic
  const canCreateProfile = userData && userData.maxProfiles && userData.profiles 
    ? userData.profiles.length < userData.maxProfiles 
    : false;

  const navLinks = (
    <>
      <Link to={`/${currentLang}/`} className="text-gray-700 hover:text-[#8b5a3c] px-2 py-1.5 rounded-md text-xs font-medium transition-colors" onClick={closeMobileMenu}>{t('nav.home')}</Link>
      <Link to={`/${currentLang}/products`} className="text-gray-700 hover:text-[#8b5a3c] px-2 py-1.5 rounded-md text-xs font-medium transition-colors" onClick={closeMobileMenu}>{t('nav.products')}</Link>
      <Link to={`/${currentLang}/about`} className="text-gray-700 hover:text-[#8b5a3c] px-2 py-1.5 rounded-md text-xs font-medium transition-colors" onClick={closeMobileMenu}>{t('nav.learnMore')}</Link>
      <Link to={`/${currentLang}/contact`} className="text-gray-700 hover:text-[#8b5a3c] px-2 py-1.5 rounded-md text-xs font-medium transition-colors" onClick={closeMobileMenu}>{t('nav.contact')}</Link>
      {isLoggedIn && userRole === 'ADMIN' && (
          <Link 
            to={`/${currentLang}/admin`} 
            className="text-gray-700 hover:text-[#8b5a3c] px-2 py-1.5 rounded-md text-xs font-medium transition-colors" 
            onClick={closeMobileMenu}
            data-testid="navbar-admin-management-link"
          >
            {t('nav.adminManagement')}
          </Link>
      )}
    </>
  );

  return (
    <nav className={navClass}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="navbar-container">
          <div className="navbar-logo-container flex-shrink-0 lg:mr-16">
            <Link to={`/${currentLang}/`} className="navbar-logo-link gap-1">
              <img src={BlackLogoTextSBS} alt="Spomen Logo" className="h-8 w-auto" />
            </Link>
          </div>


          <div className="hidden md:flex items-center space-x-3 whitespace-nowrap">
            {navLinks}
            {/* Dynamic button: Create Profile if quota available, Order Now if quota full or not logged in */}
            {isLoggedIn && canCreateProfile ? (
              <Link 
                to={`/${currentLang}/create-profile`}
                className="bg-[#c19660] hover:bg-[#8b5a3c] text-white px-3 py-1.5 rounded-full text-xs font-medium transition-all transform hover:scale-105 shadow-sm hover:shadow-md"
                onClick={closeMobileMenu}
                data-testid="navbar-dynamic-button-create-profile"
              >
                {t('nav.profileCreator')}
              </Link>
            ) : (
              <Link 
                to={`/${currentLang}/products`}
                className="bg-[#c19660] hover:bg-[#8b5a3c] text-white px-3 py-1.5 rounded-full text-xs font-medium transition-all transform hover:scale-105 shadow-sm hover:shadow-md"
                onClick={closeMobileMenu}
                data-testid="navbar-dynamic-button-order-now"
              >
                {t('nav.orderNow')}
              </Link>
            )}
          </div>

          {/* Group for Desktop-only right-side icons (Lang and User) */}
          <div className="hidden md:flex items-center ml-3 whitespace-nowrap"> 
            {/* Desktop Lang - parent 'hidden md:flex' controls visibility */}
            <div className="relative" ref={langMenuRef}>
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} 
                className="flex items-center text-gray-600 hover:text-gray-900 focus:outline-none p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              >
                <img src={selectedLanguage.icon} alt={selectedLanguage.name} className="w-4 h-auto rounded-sm mr-1" />
                <span className="text-xs font-medium">{selectedLanguage.code.toUpperCase()}</span>
                <ChevronDownIcon className={`ml-1 h-3 w-3 transition-transform duration-200 ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {isLangMenuOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none py-1">
                  {languages.map(lang => (
                    <button 
                      key={lang.code} 
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full text-left flex items-center px-3 py-2 text-xs ${currentLang === lang.code ? 'bg-[#f8f6f3] text-[#8b5a3c] font-semibold' : 'text-gray-700 hover:bg-gray-100'} transition-colors`}
                    >
                       <img src={lang.icon} alt={lang.name} className="w-4 h-auto rounded-sm mr-2" />
                       {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Desktop User/Login - parent 'hidden md:flex' controls visibility */}
            <div className="items-center ml-3">
              {isLoggedIn ? (
                <div className="relative" ref={userMenuRef}>
                  <button 
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} 
                    className="flex items-center text-xs font-medium text-gray-700 hover:text-[#8b5a3c] focus:outline-none p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                    data-testid="navbar-user-menu-toggle-desktop"
                  >
                     <UserCircleIcon className="h-4 w-4 mr-1 text-gray-500" />
                     <span className="max-w-32 truncate">{userFullName}</span>
                    <ChevronDownIcon className={`ml-1 h-3 w-3 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isUserMenuOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none py-1">
                      <Link 
                        to={`/${currentLang}/my-profile`} 
                        className="block px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        {t('nav.myProfile')}
                      </Link>
                      <Link 
                        to={`/${currentLang}/my-orders`} 
                        className="block px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        {t('nav.myOrders')}
                      </Link>
                      <button 
                        onClick={handleLogout} 
                        className="block w-full text-left px-4 py-2 text-xs text-red-700 hover:bg-red-50 hover:text-red-800 transition-colors"
                        data-testid="navbar-logout-button"
                      >
                        {t('nav.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link to={`/${currentLang}/register`} className="text-gray-700 hover:text-[#8b5a3c] px-2 py-1.5 rounded-md text-xs font-medium transition-colors">{t('nav.register')}</Link>
                  <Link to={`/${currentLang}/login`} className="bg-[#c19660] hover:bg-[#8b5a3c] text-white px-2 py-1.5 rounded-md text-xs font-medium transition-colors">{t('nav.login')}</Link>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Burger - hidden on desktop */}
          <div className="navbar-burger-container absolute right-0 md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="navbar-burger-button rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#d4a574]"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <XMarkIcon className="block h-5 w-5" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div> 
      </div>

      <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden bg-white shadow-lg`} id="mobile-menu">
        <div className="px-4 py-3 space-y-1">
          
          {/* User name if logged in */}
          {isLoggedIn && (
            <div className="flex items-center px-3 py-2 text-xs font-medium text-gray-600 border-b border-gray-100 mb-2">
              <UserCircleIcon className="h-4 w-4 mr-2 text-gray-500" />
              {userFullName}
            </div>
          )}
          
          {/* Navigation Links */}
          <Link to={`/${currentLang}/`} className="block px-3 py-2 text-xs font-medium text-gray-700 hover:text-[#8b5a3c] hover:bg-gray-50 rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.home')}</Link>
          <Link to={`/${currentLang}/products`} className="block px-3 py-2 text-xs font-medium text-gray-700 hover:text-[#8b5a3c] hover:bg-gray-50 rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.products')}</Link>
          <Link to={`/${currentLang}/about`} className="block px-3 py-2 text-xs font-medium text-gray-700 hover:text-[#8b5a3c] hover:bg-gray-50 rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.learnMore')}</Link>
          <Link to={`/${currentLang}/contact`} className="block px-3 py-2 text-xs font-medium text-gray-700 hover:text-[#8b5a3c] hover:bg-gray-50 rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.contact')}</Link>
          
          {/* Dynamic Blue Button - Mobile Version */}
          {isLoggedIn && canCreateProfile ? (
            <Link 
              to={`/${currentLang}/create-profile`} 
              className="block px-3 py-2 text-xs font-medium bg-[#c19660] text-white hover:bg-[#8b5a3c] rounded-md transition-colors" 
              onClick={closeMobileMenu}
              data-testid="navbar-dynamic-button-create-profile-mobile"
            >
              {t('nav.profileCreator')}
            </Link>
          ) : (
            <Link 
              to={`/${currentLang}/products`} 
              className="block px-3 py-2 text-xs font-medium bg-[#c19660] text-white hover:bg-[#8b5a3c] rounded-md transition-colors" 
              onClick={closeMobileMenu}
              data-testid="navbar-dynamic-button-order-now-mobile"
            >
              {t('nav.orderNow')}
            </Link>
          )}
          
          {/* Admin link for admin users */}
          {isLoggedIn && userRole === 'ADMIN' && (
            <Link to={`/${currentLang}/admin`} className="block px-3 py-2 text-xs font-medium text-gray-700 hover:text-[#8b5a3c] hover:bg-gray-50 rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.adminManagement')}</Link>
          )}
          
          {/* User Menu Items - only shown if logged in */}
          {isLoggedIn ? (
            <>
              <div className="border-t border-gray-200 my-2"></div>
              <Link to={`/${currentLang}/my-profile`} className="block px-3 py-2 text-xs font-medium text-gray-700 hover:text-[#8b5a3c] hover:bg-gray-50 rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.myProfile')}</Link>
              <Link to={`/${currentLang}/my-orders`} className="block px-3 py-2 text-xs font-medium text-gray-700 hover:text-[#8b5a3c] hover:bg-gray-50 rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.myOrders')}</Link>
              <button onClick={handleLogout} className="block w-full text-left px-3 py-2 text-xs font-medium text-red-700 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors">{t('nav.logout')}</button>
            </>
          ) : (
            <>
              {/* Login/Register for non-logged in users */}
              <Link to={`/${currentLang}/register`} className="block px-3 py-2 text-xs font-medium text-gray-700 hover:text-[#8b5a3c] hover:bg-gray-50 rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.register')}</Link>
              <Link to={`/${currentLang}/login`} className="block px-3 py-2 text-xs font-medium bg-[#c19660] text-white hover:bg-[#8b5a3c] rounded-md transition-colors" onClick={closeMobileMenu}>{t('nav.login')}</Link>
            </>
          )}
          
          {/* Language Selection */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <label htmlFor="mobile-lang-select" className="block text-xs text-gray-500 mb-1">{t('navbar.language')}</label>
            <select 
              id="mobile-lang-select"
              name="mobile-lang-select"
              className="block w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#d4a574] focus:border-[#d4a574]"
              value={currentLang}
              onChange={(e) => handleLanguageChange(e.target.value)}
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.name} 
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
